import { Link } from "react-router-dom";
import type { LadderBrief } from "@/lib/types";
import { STATE_LABEL, formatCountdown, formatDateTime } from "../utils";

interface LadderCardProps {
  ladder: LadderBrief;
  remaining?: number;
}

const STATE_BADGE: Record<LadderBrief["state"], string> = {
  ready: "bg-secondary-100 text-secondary-900",
  open: "bg-accent-100 text-accent-900",
  closed: "bg-background-200 text-foreground-500",
};

export default function LadderCard({ ladder, remaining }: LadderCardProps) {
  const open = ladder.state === "open";
  const progress = ladder.slot_cnt > 0 ? (ladder.join_cnt / ladder.slot_cnt) * 100 : 0;

  return (
    <Link
      to={`/ladder/${ladder.no}`}
      className="group flex flex-col rounded-xl border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300 hover:bg-background-100/60"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATE_BADGE[ladder.state]}`}
          >
            {STATE_LABEL[ladder.state]}
          </span>
          <span className="inline-block rounded-full bg-background-100 px-2.5 py-0.5 text-xs font-medium text-foreground-500">
            {ladder.reveal_mode === 0 ? "즉시공개" : "종료 후 공개"}
          </span>
          {ladder.is_joined === 1 && (
            <span className="inline-block rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-semibold text-primary-700">
              참여완료
            </span>
          )}
        </div>
      </div>

      <h3 className="mt-3 font-heading text-base font-bold text-foreground-950 group-hover:text-primary-700">
        {ladder.subject || ladder.title || "사다리게임"}
      </h3>

      <p className="mt-1.5 line-clamp-2 text-sm text-foreground-600">
        {ladder.content || "참여해서 경품에 도전해 보세요."}
      </p>

      <p className="mt-3 text-xs text-foreground-500">
        {formatDateTime(ladder.start_date)} ~ {formatDateTime(ladder.end_date)}
      </p>

      {/* 참여 현황 */}
      <div className="mt-4">
        <div className="flex items-center justify-between text-xs text-foreground-500">
          <span>
            참여 <strong className="text-foreground-800">{ladder.join_cnt}</strong>
            <span className="text-foreground-400"> / {ladder.slot_cnt}</span>
          </span>
          <span>남은 칸 {ladder.left_cnt}</span>
        </div>
        <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-background-100">
          <div
            className="h-full rounded-full bg-accent-500 transition-all"
            style={{ width: `${Math.min(100, progress)}%` }}
          />
        </div>
      </div>

      {/* 진행중 카운트다운 */}
      {open && typeof remaining === "number" && (
        <div className="mt-4 flex items-center justify-between rounded-lg bg-background-100 px-3 py-2">
          <span className="text-xs text-foreground-500">종료까지</span>
          <span className="font-heading text-sm font-bold tabular-nums text-accent-700">
            {formatCountdown(remaining)}
          </span>
        </div>
      )}
    </Link>
  );
}